import React, { useState, useEffect } from 'react';
import { axios } from '../../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const TL = (v) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(v || 0);

export default function MuhasebeDashboard() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/muhasebe/dashboard')
      .then(r => { if (r.data.success) setData(r.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page-loader"><div className="spinner" /></div>;
  if (!data)   return <div className="card"><div className="empty-state"><p>Veri yüklenemedi</p></div></div>;

  const AYLAR = ['Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'];
  const chartData = (data.aylik_gelir || []).map(r => ({
    ay: AYLAR[parseInt(r.ay.slice(5,7)) - 1],
    gelir: parseFloat(r.gelir),
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="animate-fadeInUp">
        <h2 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: '1.4rem' }}>💰 Muhasebe Paneli</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 4 }}>Finansal özet ve anlık durum</p>
      </div>

      <div className="animate-fadeInUp stagger-1 muh-grid-4" style={{ gap: 14 }}>
        {[
          { label: 'Bu Ay Gelir',   value: TL(data.gelir_bu_ay),  color: '#10B981', icon: '📈', bg: '#D1FAE5' },
          { label: 'Bu Ay Gider',   value: TL(data.gider_bu_ay),  color: '#EF4444', icon: '📉', bg: '#FEE2E2' },
          { label: 'Bu Ay Kâr',     value: TL(data.kar_bu_ay),    color: data.kar_bu_ay >= 0 ? '#3B82F6' : '#EF4444', icon: '⚖️', bg: '#DBEAFE' },
          { label: 'Kasa Bakiyesi', value: TL(data.kasa_bakiye),  color: '#F59E0B', icon: '🏦', bg: '#FEF3C7' },
        ].map((s, i) => (
          <div key={i} className="card" style={{ background: s.bg, border: `1.5px solid ${s.color}30`, padding: 18 }}>
            <div style={{ fontSize: '1.8rem', marginBottom: 8 }}>{s.icon}</div>
            <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: '1.25rem', color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '0.75rem', color: s.color, fontWeight: 600, marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div className="animate-fadeInUp stagger-2 muh-grid-3" style={{ gap: 14 }}>
        {[
          { label: 'Yıllık Gelir',    value: TL(data.yillik_gelir),           color: '#10B981', icon: '📊' },
          { label: 'Gecikmiş Alacak', value: TL(data.gecikmiş_tutar),          color: '#EF4444', icon: '⚠️' },
          { label: 'Aktif Sözleşme',  value: `${data.aktif_sozlesme} öğrenci`, color: '#3B82F6', icon: '📋' },
        ].map((s, i) => (
          <div key={i} className="stat-card">
            <div style={{ fontSize: '1.4rem', marginBottom: 6 }}>{s.icon}</div>
            <div className="stat-number" style={{ color: s.color, fontSize: '1.25rem' }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="muh-two-col" style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 16 }}>
        {chartData.length > 0 && (
          <div className="card animate-fadeInUp stagger-2">
            <div className="section-title" style={{ marginBottom: 16 }}>📈 Son 6 Ay Gelir</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} barSize={36}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="ay" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                <YAxis tickFormatter={v => (v/1000).toFixed(0)+'K'} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                <Tooltip formatter={v => [TL(v), 'Gelir']} />
                <Bar dataKey="gelir" fill="#10B981" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="card animate-fadeInUp stagger-3" style={{ padding: 0 }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="section-title">⚠️ Gecikmiş Alacaklar</div>
            <span className="badge" style={{ background: '#FEE2E2', color: '#EF4444' }}>{data.gecikmiş_sayi}</span>
          </div>
          {!data.gecikmiş_liste?.length ? (
            <div className="empty-state" style={{ padding: 32 }}><p>✅ Gecikmiş taksit yok</p></div>
          ) : (
            <div style={{ overflowY: 'auto', maxHeight: 240 }}>
              {data.gecikmiş_liste.map((g, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 16px', borderBottom: '1px solid var(--border)',
                }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.875rem' }}>{g.ad} {g.soyad}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                      {new Date(g.vade_tarihi).toLocaleDateString('tr-TR')} · {g.gecikme_gun} gün
                    </div>
                  </div>
                  <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, color: '#EF4444', fontSize: '0.9rem' }}>{TL(g.tutar)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
